import { SequenceCell } from '@phaser/minigames/sequences/Cell';
import { DEPTH } from '@phaser/data/depth';
import { Game } from '@phaser/scenes/Game';

const NUMBERS = ['OP', 'MJ', 'CV', 'UF', 'ZG', 'KJ'];
const CELL_SIZE = 70;
const GRID_SIZE = 5;

export class SequencesMinigame {
  private _grid: { cell: SequenceCell; seq: string; x: number; y: number; }[] = [];

  private _sequenceTexts: Phaser.GameObjects.Text[] = [];
  private _container?: Phaser.GameObjects.Container;
  private _activateMode: 'x' | 'y' = 'y';
  private _scene: Game = Game.current;
  private _sequence: string[] = [];
  private _sequenceIndex = 0;

  constructor() {}

  public start() {
    this._placeNumbers();
    this._computeSequence();
    this._displaySequence();
  }

  public restart() {
    if (!this._scene.minigameSystem.hasTimeLeft) return;
    this.destroy();
    this.start();
  }

  public destroy() {
    this._container?.destroy();
    this._grid.forEach(g => g.cell.destroy());
    this._sequenceTexts.forEach(t => t.destroy());

    this._grid = [];
    this._sequence = [];
    this._sequenceTexts = [];
    this._sequenceIndex = 0;

    this._activateMode = 'y';
  }

  public activate({ x, y }: { x?: number, y?: number }) {
    this._grid.forEach(g => {
      if (g.x === x) return g.cell.markSelectable(true);
      if (g.y === y) return g.cell.markSelectable(true);
      g.cell.markSelectable(false);
    });

    this._activateMode = this._activateMode === 'x' ? 'y' : 'x';

    if (x !== undefined && y !== undefined) {
      const currentSequence = this._sequence[this._sequenceIndex];
      const currentCell = this._grid.find(g => g.x === x && g.y === y)?.seq;

      this._sequenceTexts[this._sequenceIndex]
        ?.setColor(currentSequence === currentCell ? '#00DDFF' : '#FF0000');
      this._sequenceIndex++;

      if (currentSequence !== currentCell) this.restart();
      if (this._sequenceIndex >= 5) this._scene.minigameSystem.success();
    }
  }

  private _placeNumbers() {
    const camera = this._scene.cameras.main;
    const width = camera.displayWidth;
    const height = camera.displayHeight;

    const maxSize = CELL_SIZE * GRID_SIZE;

    this._container = this._scene.add.container(width / 2, height / 2 - 46).setDepth(DEPTH.MINIGAME);

    const background = this._scene.add.rectangle(0, 0, maxSize, maxSize, 0x282828)
      .setStrokeStyle(1, 0x413F3D);

    this._container.add(background);

    for (let x = 0; x < GRID_SIZE; x += 1) {
      for (let y = 0; y < GRID_SIZE; y += 1) {
        const xPos = x * CELL_SIZE - maxSize / 2 + CELL_SIZE / 2;
        const yPos = y * CELL_SIZE- maxSize / 2 + CELL_SIZE / 2;
        const seq = NUMBERS[Math.floor(Math.random() * NUMBERS.length)];

        const cell = new SequenceCell(this, xPos, yPos, CELL_SIZE, { x, y});
        cell.setText(seq);
        this._container.add(cell.container);

        this._grid.push({ cell, seq, x, y });
      }
    }

    this.activate({ y: 0 });
  }

  private _computeSequence() {
    const usedCells: { x: number; y: number }[] = [];
    let activateMode: 'x' | 'y' = 'y';
    let activateAt = 0;

    const getRandomCoords = () => {
      const { x, y } = {
        x: activateMode === 'y' ? Math.floor(Math.random() * 5) : activateAt,
        y: activateMode === 'x' ? Math.floor(Math.random() * 5) : activateAt,
      };

      if (usedCells.find(c => c.x === x && c.y === y)) return getRandomCoords();

      return { x, y };
    };

    for (let i = 0; i < 5; i++) {
      const { x, y } = getRandomCoords();

      usedCells.push({ x, y });

      activateMode = activateMode === 'x' ? 'y' : 'x';
      activateAt = activateMode === 'x' ? x : y;
    }

    this._sequence = usedCells.map(c => this._grid.find(g => g.x === c.x && g.y === c.y)!.seq!);
  }

  private _displaySequence() {
    const camera = this._scene.cameras.main;
    const width = camera.displayWidth;
    const height = camera.displayHeight;

    const xPos = (width / 2) - (CELL_SIZE * 5 / 2) + CELL_SIZE / 2;
    const yPos = (height / 2) + (CELL_SIZE * 5 / 2);

    this._sequence.forEach((seq, i) => {
      this._sequenceTexts.push(this._scene.add.text(xPos + i * CELL_SIZE, yPos, seq, {
        fontFamily: 'Ascentian',
        fontSize: '36px',
        resolution: 2,
      }).setOrigin(0.5).setDepth(DEPTH.MINIGAME));
    });
  }
}